import type { ICopilotSseParsedEvent } from 'helpers/copilotStreamClient';

import { MESSAGE_STATUS_OPTIONS } from 'constants/copilot';

/**
 * Coalesces Copilot SSE `chunk` events before they reach Redux.
 *
 * Every chunk dispatch re-renders the streaming message, and rendering it means
 * re-parsing the whole markdown document (react-markdown has no incremental
 * parse). That cost grows with the response, so it is `O(chunks × length)`
 * overall. It is survivable while chunks trickle in live, but the backend
 * replays every buffered event at once when we reconnect with `last_event_id`
 * (e.g. the user switched to another chat mid-run and came back). Hundreds of
 * back-to-back renders of a long answer lock the main thread, and because the
 * stream keeps producing while we are blocked, the UI stays frozen until the
 * run ends.
 *
 * Chunk `message` values are deltas the reducer concatenates
 * (`RECEIVE_COPILOT_MESSAGE` in `reducers/copilot.js`), so consecutive chunks
 * for the same message can be merged into one payload with the text joined —
 * no content is lost, only intermediate frames.
 */
export interface ICopilotStreamChunk {
  payload: Record<string, unknown>;
  meta: ICopilotSseParsedEvent;
}

/** Upper bound on streaming re-render frequency (~20 fps), still smooth to the eye. */
export const COPILOT_STREAM_FLUSH_INTERVAL_MS = 50;

/**
 * Ceiling for the self-tuning back-off. Rendering a long answer costs more the
 * longer it gets, so the queue spaces flushes at least as far apart as the last
 * flush took — leaving roughly half the main thread for typing and scrolling —
 * without letting the text visibly lag behind the stream.
 */
export const COPILOT_STREAM_MAX_FLUSH_INTERVAL_MS = 500;

const getChunkMessageData = (
  payload: Record<string, unknown> | null | undefined
): Record<string, unknown> | null => {
  const data = payload?.data;

  return data && typeof data === 'object' && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : null;
};

/** Message uuid on a camelized stream payload, without the `camelize` deep clone. */
export const getCopilotStreamChunkMessageUuid = (
  payload: Record<string, unknown> | null | undefined
): string | undefined => {
  const uuid = getChunkMessageData(payload)?.uuid;

  return typeof uuid === 'string' && uuid.length > 0 ? uuid : undefined;
};

/**
 * Only plain text-delta rows merge. `thinking` rows append a separate plan step
 * (each one is its own entry in `thinkingMessages`) and name updates take a
 * different reducer path, so both stay untouched.
 */
const isMergeableChunk = (chunk: ICopilotStreamChunk): boolean => {
  if (chunk.payload?.msg === 'copilot_name_updated') {
    return false;
  }

  const data = getChunkMessageData(chunk.payload);
  if (!data || !getCopilotStreamChunkMessageUuid(chunk.payload)) {
    return false;
  }

  return (
    data.status === MESSAGE_STATUS_OPTIONS.IN_PROGRESS ||
    data.status === MESSAGE_STATUS_OPTIONS.FINISHED_SUCCESSFULLY
  );
};

const canMergeChunks = (
  previous: ICopilotStreamChunk,
  next: ICopilotStreamChunk
): boolean =>
  isMergeableChunk(previous) &&
  isMergeableChunk(next) &&
  getCopilotStreamChunkMessageUuid(previous.payload) ===
    getCopilotStreamChunkMessageUuid(next.payload);

/**
 * Later fields win (status, `created`, `dataJson`, …) so the merged chunk
 * matches the state the last event would have produced; only `message` is
 * concatenated, mirroring the reducer's own append.
 */
const mergeChunkPair = (
  previous: ICopilotStreamChunk,
  next: ICopilotStreamChunk
): ICopilotStreamChunk => {
  const previousData = getChunkMessageData(previous.payload) ?? {};
  const nextData = getChunkMessageData(next.payload) ?? {};
  const previousMessage =
    typeof previousData.message === 'string' ? previousData.message : '';
  const nextMessage =
    typeof nextData.message === 'string' ? nextData.message : '';

  return {
    meta: next.meta,
    payload: {
      ...next.payload,
      data: {
        ...nextData,
        message: `${previousMessage}${nextMessage}`
      }
    }
  };
};

/** Folds runs of consecutive same-message text chunks into single payloads. */
export const mergeCopilotStreamChunks = (
  chunks: ICopilotStreamChunk[]
): ICopilotStreamChunk[] =>
  chunks.reduce<ICopilotStreamChunk[]>((merged, chunk) => {
    const previous = merged[merged.length - 1];

    if (previous && canMergeChunks(previous, chunk)) {
      merged[merged.length - 1] = mergeChunkPair(previous, chunk);
      return merged;
    }

    merged.push(chunk);
    return merged;
  }, []);

export interface ICopilotStreamChunkQueueOptions {
  /** Receives the merged chunks, in arrival order, for one flush. */
  onFlush: (chunks: ICopilotStreamChunk[]) => void;
  intervalMs?: number;
  maxIntervalMs?: number;
  schedule?: (callback: () => void, delayMs: number) => unknown;
  cancel?: (timerId: unknown) => void;
  now?: () => number;
}

export interface ICopilotStreamChunkQueue {
  enqueue: (chunk: ICopilotStreamChunk) => void;
  /** Dispatches anything queued right now (used before terminal events). */
  flush: () => void;
  /** Drops queued chunks and any pending timer without dispatching. */
  clear: () => void;
}

/**
 * Throttled queue for a single run's chunk stream: the first chunk after an
 * idle gap flushes immediately (so the first token stays snappy), and anything
 * arriving inside `intervalMs` is batched into one trailing flush.
 */
export const createCopilotStreamChunkQueue = ({
  onFlush,
  intervalMs = COPILOT_STREAM_FLUSH_INTERVAL_MS,
  maxIntervalMs = COPILOT_STREAM_MAX_FLUSH_INTERVAL_MS,
  schedule = (callback, delayMs) => setTimeout(callback, delayMs),
  cancel = timerId => clearTimeout(timerId as ReturnType<typeof setTimeout>),
  now = () => Date.now()
}: ICopilotStreamChunkQueueOptions): ICopilotStreamChunkQueue => {
  let queued: ICopilotStreamChunk[] = [];
  let timerId: unknown = null;
  let lastFlushAtMs: number | null = null;
  let lastFlushDurationMs = 0;

  const cancelTimer = () => {
    if (timerId !== null) {
      cancel(timerId);
      timerId = null;
    }
  };

  /** Renders synchronously (React 17), so the last flush's cost sets the pace. */
  const getEffectiveIntervalMs = () =>
    Math.min(Math.max(intervalMs, lastFlushDurationMs), maxIntervalMs);

  const flush = () => {
    cancelTimer();

    if (queued.length === 0) {
      return;
    }

    const chunks = queued;
    queued = [];
    const startedAtMs = now();
    try {
      onFlush(mergeCopilotStreamChunks(chunks));
    } finally {
      lastFlushAtMs = now();
      lastFlushDurationMs = Math.max(0, lastFlushAtMs - startedAtMs);
    }
  };

  return {
    enqueue: chunk => {
      queued.push(chunk);

      if (timerId !== null) {
        return;
      }

      const effectiveIntervalMs = getEffectiveIntervalMs();
      const elapsedMs =
        lastFlushAtMs === null ? effectiveIntervalMs : now() - lastFlushAtMs;

      if (elapsedMs >= effectiveIntervalMs) {
        flush();
        return;
      }

      timerId = schedule(() => {
        timerId = null;
        flush();
      }, effectiveIntervalMs - elapsedMs);
    },
    flush,
    clear: () => {
      cancelTimer();
      queued = [];
    }
  };
};
